import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Users, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlatformAdminCoaches = () => {
  const { getAuthHeaders } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchCoaches = async () => {
      try {
        const response = await axios.get(`${API}/platform-admin/coaches`, { headers: getAuthHeaders() });
        setCoaches(response.data);
      } catch (error) {
        console.error('Failed to fetch coaches:', error);
      }
      setLoading(false);
    };
    fetchCoaches();
  }, [getAuthHeaders]);

  const filteredCoaches = coaches.filter(c =>
    c.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.business_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="platform-admin-coaches">
      <div>
        <h1 className="font-heading text-3xl uppercase tracking-tight">All Coaches</h1>
        <p className="text-muted-foreground">{coaches.length} coaches registered</p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
        <Input
          placeholder="Search coaches..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-transparent"
        />
      </div>

      {/* Coaches List */}
      {filteredCoaches.length === 0 ? (
        <div className="bg-card border border-border/40 p-12 rounded-sm text-center">
          <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">{search ? 'No coaches found' : 'No coaches registered yet'}</p>
        </div>
      ) : (
        <div className="bg-card border border-border/40 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/30">
              <tr>
                <th className="text-left p-4 text-sm uppercase tracking-wider text-muted-foreground">Coach</th>
                <th className="text-left p-4 text-sm uppercase tracking-wider text-muted-foreground">Business</th>
                <th className="text-left p-4 text-sm uppercase tracking-wider text-muted-foreground">Plan</th>
                <th className="text-left p-4 text-sm uppercase tracking-wider text-muted-foreground">Athletes</th>
                <th className="text-left p-4 text-sm uppercase tracking-wider text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {filteredCoaches.map((coach) => (
                <tr key={coach.id} className="hover:bg-muted/20">
                  <td className="p-4">
                    <div>
                      <p className="font-medium">{coach.full_name}</p>
                      <p className="text-sm text-muted-foreground">{coach.email}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <p>{coach.business_name || '-'}</p>
                    <p className="text-sm text-muted-foreground capitalize">{coach.sport}</p>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs uppercase ${
                      coach.subscription?.plan === 'unlimited' ? 'bg-purple-500/20 text-purple-400' :
                      coach.subscription?.plan === 'pro' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {coach.subscription?.plan || 'Starter'}
                    </span>
                  </td>
                  <td className="p-4">
                    <p>{coach.athlete_count || 0}</p>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs uppercase ${
                      coach.subscription?.status === 'active' 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {coach.subscription?.status || 'Active'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PlatformAdminCoaches;
